/**
 * ajax请求封装
 * 1、调用客户端的加解密方法
 * 2、统一处理请求头，将post转成form-urlencoded的形式
 * 3、ajax请求默认设置，如过期时间
 * 4、统一处理响应信息
 */
import axios from 'axios'
import config from '@/config/index'
import { storeToRefs } from 'pinia'
import { errorMessage } from '@/utils/message'
import useUserStore from '@/stores/userInfo'
import 'nprogress/nprogress.css'

/**
 *  自定义配置新建一个 axios 实例
 * @type {AxiosInstance}
 */
const service = axios.create({
  timeout: 60000, // 请求超时时间
  baseURL: config.baseApi
})

// 请求拦截器
service.interceptors.request.use((reqConfig) => {
  return reqConfig
})

// 响应拦截器
service.interceptors.response.use(
  (response) => {
    // 如果返回的是二进制流
    if (response.config.responseType === 'blob') {
      return response.data
    }
    const { errcode, data, errinfo } = response.data
    if (errcode === '0') {
      return data
    } else if (errcode === '4101') {
      // 登陆信息不存在,请重新登陆
      const userStore = useUserStore()
      userStore.emptyUserInfo()
      errorMessage(errinfo, { route: '/' })
    } else {
      errorMessage(errinfo)
      return Promise.reject(errinfo)
    }
  },
  (err) => {
    errorMessage('网络错误: ' + err)
    return Promise.reject(err)
  }
)

/**
 * 核心函数，可通过它处理一切请求数据，并做横向扩展
 * @param {url} 请求地址
 * @param {params} 请求参数
 * @param {options} 请求配置，针对当前本次请求；
 * @param mock 本次是否请求mock而非线上
 */
function request(options) {
  const { userInfo, peerid, sessionid } = storeToRefs(useUserStore())
  // 统一给接口添加peerid和sessionid
  if (options.takePeeridAndSessionid) {
    options.data.peerid = peerid.value
    options.data.sessionid = userInfo.value.sessionid || sessionid.value
  }

  // options.method默认为get
  options.method = options.method || 'get'
  // get请求使用params字段
  if (options.method.toLowerCase() === 'get') {
    !options.params && (options.params = options.data)
  }

  // 局部接口mock
  if (config.env === 'prod') {
    service.defaults.baseURL = config.baseApi
  } else {
    let isMock = false
    if (Object.prototype.hasOwnProperty.call(options, 'mock')) {
      isMock = !!options.mock
    } else {
      isMock = !!config.mock
    }

    // console.log('isMock:', isMock)
    service.defaults.baseURL = isMock ? config.mockApi : config.baseApi
  }

  // 错误提示文案
  return service(options)
}

// 适配this.$request.get()调用
const methodList = ['get', 'post', 'patch', 'delete', 'put']
methodList.forEach((item) => {
  request[item] = (url, data, options) => {
    return request({
      url,
      data,
      method: item,
      ...options
    })
  }
})
export default request
